/**
 * Document types: their labels, who signs them, and which ones a user may
 * pick in a "generate document" picker.
 *
 * `disclosure_acknowledgment` is the Buyer's Acknowledgment of Receipt of the
 * seller's condition report (RECR / VLDR). The server generates it with the
 * purchase agreement and puts it in the post-acceptance envelope. Only the
 * buyer (and the buyer's spouse) sign it; the seller has no signature row.
 */

export const DISCLOSURE_ACKNOWLEDGMENT = 'disclosure_acknowledgment';

/** Generic label; the document's own title names the report it acknowledges. */
export const DISCLOSURE_ACKNOWLEDGMENT_LABEL = "Buyer's Acknowledgment of Receipt";

export const DOCUMENT_TYPE_LABELS = {
  purchase_agreement: 'Purchase Agreement',
  counter_offer: 'Counter Offer',
  addendum: 'Addendum',
  seller_disclosure: 'Seller Disclosure',
  lead_paint_disclosure: 'Lead Paint Disclosure',
  well_septic_addendum: 'Well & Septic Addendum',
  radon_disclosure: 'Radon Disclosure',
  mud_disclosure: 'MUD Disclosure',
  financing_addendum: 'Financing Addendum',
  seller_listing_agreement: 'Listing Agreement',
  contingency_notice: 'Contingency Notice',
  wi_condo_recr_addendum: 'Condominium Addendum',
  [DISCLOSURE_ACKNOWLEDGMENT]: DISCLOSURE_ACKNOWLEDGMENT_LABEL,
};

/** Signed by the buyer side only: the seller never has a signature row. */
const BUYER_SIGNED_ONLY_TYPES = new Set([DISCLOSURE_ACKNOWLEDGMENT]);

/** Made by the server alongside another document, never picked by a user. */
const AUTO_GENERATED_TYPES = new Set([DISCLOSURE_ACKNOWLEDGMENT]);

/** Signer roles as people read them. */
export const SIGNER_ROLE_LABELS = {
  buyer: 'Buyer',
  buyer_spouse: "Buyer's spouse",
  seller: 'Seller',
  seller_spouse: "Seller's spouse",
};

const typeOf = (docOrType) =>
  typeof docOrType === 'string' ? docOrType : docOrType?.document_type;

/**
 * A readable label for a document type. An unmapped type reads as words,
 * never as a raw key such as "contingency_notice" (QA 2026-10-16).
 */
export const formatDocType = (type) => {
  if (!type) return '';
  if (DOCUMENT_TYPE_LABELS[type]) return DOCUMENT_TYPE_LABELS[type];
  const words = String(type).replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The document's own title when it has one, else its type's label. */
export const documentDisplayName = (doc) =>
  (doc?.title && String(doc.title).trim()) || formatDocType(doc?.document_type);

/** True for types only the buyer side signs (no seller signature row). */
export const isBuyerSignedOnly = (docOrType) => BUYER_SIGNED_ONLY_TYPES.has(typeOf(docOrType));

/** True for types the server generates itself (never offered in a picker). */
export const isAutoGenerated = (docOrType) => AUTO_GENERATED_TYPES.has(typeOf(docOrType));

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Whether `userId` may be offered a sign action on `doc`.
 *
 * Buyer-signed-only documents: only someone with their own pending signature
 * row (the buyer or the buyer's spouse). Other documents keep the existing
 * behaviour.
 *
 * @param {object|null} doc
 * @param {number|string|null} userId
 * @returns {boolean}
 */
export function canUserSignDocument(doc, userId) {
  if (!doc) return false;
  if (!isBuyerSignedOnly(doc)) return true;
  const signatures = Array.isArray(doc.signatures) ? doc.signatures : [];
  return signatures.some((sig) => sameId(sig.signer_id, userId) && sig.status === 'pending');
}

/**
 * Template picker options without the types the server generates itself.
 *
 * @param {Array<{value: string, label: string}>} options
 */
export const generatableTemplateOptions = (options) =>
  (Array.isArray(options) ? options : []).filter((opt) => !isAutoGenerated(opt?.value));

/** "Buyer", "Buyer's spouse", … for a signature row without a name. */
export const signerRoleLabel = (role) =>
  SIGNER_ROLE_LABELS[role] || (role ? formatDocType(role) : 'Signer');
